import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Undo2, Share2, Flag, UserCog, XOctagon, Maximize2 } from "lucide-react";
import { api, ZONES, DISTANCES } from "@/lib/handball";
import GoalGrid from "@/components/GoalGrid";
import GoalFrame from "@/components/GoalFrame";
import HandballCourt from "@/components/HandballCourt";
import ShareDialog from "@/components/ShareDialog";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import StatsView from "@/components/StatsView";
import StatsFilters from "@/components/StatsFilters";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function SessionRegister() {
  const { gkId, sessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [goalkeeper, setGoalkeeper] = useState(null);
  const [allGoalkeepers, setAllGoalkeepers] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [session, setSession] = useState(null);
  const [shots, setShots] = useState([]);
  const [stats, setStats] = useState(null);

  const [zone, setZone] = useState(null);
  const [distance, setDistance] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [saved, setSaved] = useState(null);
  const [logShooter, setLogShooter] = useState(false);
  const [shooter, setShooter] = useState("");
  const [contact, setContact] = useState(null);
  const [lob, setLob] = useState(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [finalScore, setFinalScore] = useState("");
  const [matchNotes, setMatchNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState({ zones: [], distances: [], shooter: null, contact: null, lob: null });
  const [matchMode, setMatchMode] = useState(() => {
    try {
      return localStorage.getItem("mvstats.matchMode") === "1";
    } catch {
      return false;
    }
  });
  const toggleMatchMode = () => {
    setMatchMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("mvstats.matchMode", next ? "1" : "0");
      } catch {
        /* ignore quota */
      }
      return next;
    });
  };

  const loadAll = useCallback(async () => {
    const [gksRes, sessRes, shotsRes, statsRes] = await Promise.all([
      api.get("/goalkeepers"),
      api.get(`/sessions/${sessId}`),
      api.get("/shots", { params: { session_id: sessId } }),
      api.get("/stats", { params: { goalkeeper_id: gkId, session_id: sessId } }),
    ]);
    const gk = gksRes.data.find((g) => g.id === gkId);
    const sess = sessRes.data;
    if (!gk || !sess) {
      toast.error("Session hittades ej");
      navigate("/");
      return;
    }
    setGoalkeeper(gk);
    // If the session has a participating list, limit the switcher to those
    // goalkeepers. Otherwise keep the full accessible list (legacy sessions).
    const partIds = Array.isArray(sess?.participating_goalkeeper_ids)
      ? sess.participating_goalkeeper_ids
      : null;
    if (partIds && partIds.length > 0) {
      const filtered = gksRes.data.filter((g) => partIds.includes(g.id));
      setAllGoalkeepers(filtered.length > 0 ? filtered : gksRes.data);
    } else {
      setAllGoalkeepers(gksRes.data);
    }
    setSession(sess);
    setShots(shotsRes.data);
    setStats(statsRes.data);
    setLoaded(true);
  }, [gkId, sessId, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshStats = useCallback(async () => {
    const params = new URLSearchParams({ goalkeeper_id: gkId, session_id: sessId });
    filters.zones.forEach((z) => params.append("zone", z));
    filters.distances.forEach((d) => params.append("distance", d));
    if (filters.shooter != null) params.set("shooter", String(filters.shooter));
    if (filters.contact != null) params.set("contact", String(filters.contact));
    if (filters.lob != null) params.set("lob", String(filters.lob));
    const [shotsRes, statsRes] = await Promise.all([
      api.get("/shots", { params: { session_id: sessId } }),
      api.get(`/stats?${params.toString()}`),
    ]);
    setShots(shotsRes.data);
    setStats(statsRes.data);
  }, [gkId, sessId, filters]);

  // Refetch stats whenever filters change
  useEffect(() => {
    if (loaded) refreshStats();
  }, [filters, loaded, refreshStats]);

  const cfg = user?.stats_config || {
    zone: true,
    distance: true,
    placement: true,
    shooter: true,
  };

  const isStraff = cfg.distance && distance === "7m";
  const isMiss = cfg.placement && placement != null && placement >= 10;
  const shooterValid =
    !cfg.shooter || !logShooter || (shooter !== "" && +shooter >= 1 && +shooter <= 99);
  const inputReady =
    (!cfg.zone || isStraff || zone) &&
    (!cfg.distance || distance) &&
    (!cfg.placement || placement) &&
    (isMiss || saved !== null) &&
    shooterValid;

  // Auto-scroll between input sections after each selection, so coaches on the
  // bench never need to scroll manually mid-rally.
  const sectionRefs = {
    zone: useRef(null),
    distance: useRef(null),
    shooter: useRef(null),
    placement: useRef(null),
    contact: useRef(null),
    lob: useRef(null),
    saved: useRef(null),
    submit: useRef(null),
  };
  const scrollNextAfter = useCallback(
    (key) => {
      const order = ["zone", "distance", "shooter", "placement", "contact", "lob", "saved", "submit"];
      const idx = order.indexOf(key);
      if (idx < 0) return;
      for (let i = idx + 1; i < order.length; i++) {
        const next = order[i];
        const enabled =
          next === "submit" || next === "saved"
            ? true
            : next === "shooter"
              ? cfg.shooter && logShooter
              : cfg[next];
        if (enabled && sectionRefs[next]?.current) {
          try {
            sectionRefs[next].current.scrollIntoView({ behavior: "smooth", block: "start" });
          } catch {
            /* older safari fallback */
            sectionRefs[next].current.scrollIntoView();
          }
          return;
        }
      }
    },
    [cfg.distance, cfg.shooter, cfg.placement, cfg.contact, cfg.lob, logShooter]
  );

  const handleZoneChange = (z) => {
    setZone(z);
    if (z) scrollNextAfter("zone");
  };

  const selectDistance = (d) => {
    setDistance(d);
    if (d === "7m") setZone(null);
    scrollNextAfter("distance");
  };

  const selectPlacement = (p) => {
    setPlacement(p);
    if (p >= 10) {
      // Miss: no save/no goal – lock saved to false silently
      setSaved(false);
      scrollNextAfter("saved"); // skip past contact/lob/saved to submit
    } else {
      scrollNextAfter("placement");
    }
  };

  const chooseContact = (v) => {
    setContact(v);
    scrollNextAfter("contact");
  };

  const chooseLob = (v) => {
    setLob(v);
    scrollNextAfter("lob");
  };

  const chooseSaved = (v) => {
    setSaved(v);
    scrollNextAfter("saved");
  };

  const registerShot = async () => {
    if (goalkeeper?.role === "viewer") {
      toast.error("Du har läsrättigheter – kan inte logga skott");
      return;
    }
    if (!inputReady || saving) return;
    setSaving(true);
    try {
      await api.post("/shots", {
        session_id: sessId,
        goalkeeper_id: gkId,
        zone: cfg.zone && !isStraff ? zone : null,
        distance: cfg.distance ? distance : null,
        placement: cfg.placement ? placement : null,
        saved: isMiss ? false : saved,
        shooter: logShooter ? Number(shooter) : null,
        contact: cfg.contact ? contact : null,
        lob: cfg.lob ? lob : null,
      });
      toast.success(
        isMiss ? "Skott utanför registrerat" : saved ? "Räddning registrerad" : "Mål registrerat"
      );
      setZone(null);
      setDistance(null);
      setPlacement(null);
      setSaved(null);
      setLogShooter(false);
      setShooter("");
      setContact(null);
      setLob(null);
      refreshStats();
      const top = sectionRefs.zone.current;
      if (top) {
        window.dispatchEvent(new Event("appheader-hide"));
        top.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      toast.error("Kunde inte spara skott");
    } finally {
      setSaving(false);
    }
  };

  const undoLast = async () => {
    const last = shots[shots.length - 1];
    if (!last) return;
    await api.delete(`/shots/${last.id}`);
    toast.info("Senaste skott ångrat");
    refreshStats();
  };

  const cancelSession = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await api.delete(`/sessions/${sessId}`);
      toast.success("Session avbruten – all data borttagen");
      navigate(`/gk/${gkId}`);
    } catch {
      toast.error("Kunde inte avbryta sessionen");
      setCancelling(false);
    }
  };

  const completeMatch = async () => {
    try {
      await api.post(`/sessions/${sessId}/complete`, {
        final_score: finalScore.trim() || null,
        notes: matchNotes.trim() || null,
      });
      toast.success("Matchen är avslutad");
      navigate(`/gk/${gkId}`);
    } catch {
      toast.error("Kunde inte avsluta matchen");
    }
  };

  const switchGoalkeeper = async (newGkId) => {
    if (!newGkId || newGkId === gkId || switching) return;
    setSwitching(true);
    try {
      // Same session – just tag subsequent shots to the new goalkeeper.
      navigate(`/gk/${newGkId}/session/${sessId}/register`);
      toast.success("Bytte målvakt – nästa skott loggas på den nya profilen");
    } finally {
      setSwitching(false);
    }
  };

  const totalShots = useMemo(() => shots.length, [shots]);

  if (!loaded) {
    return (
      <div className="min-h-screen">
        <AppHeader subtitle="Laddar…" backTo={`/gk/${gkId}`} backLabel="Meny" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen${matchMode ? " match-mode" : ""}`}>
      <AppHeader
        subtitle={`${goalkeeper.name} · ${session.name}`}
        backTo={`/gk/${gkId}`}
        backLabel="Meny"
        hideOnScrollDown
        right={
          <>
            {!session.completed && goalkeeper.role !== "viewer" && allGoalkeepers.length > 1 && (
              <Select
                value={gkId}
                onValueChange={switchGoalkeeper}
                disabled={switching}
              >
                <SelectTrigger
                  data-testid="switch-goalkeeper-select"
                  className="h-11 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white w-[220px]"
                >
                  <UserCog size={14} className="mr-2 text-[#34C759]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#27272A] text-white">
                  {allGoalkeepers.map((g) => (
                    <SelectItem
                      key={g.id}
                      value={g.id}
                      data-testid={`switch-goalkeeper-option-${g.id}`}
                    >
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {!session.completed && goalkeeper.role !== "viewer" && (
              <Button
                data-testid="cancel-session-btn"
                onClick={() => setCancelOpen(true)}
                variant="outline"
                className="h-11 rounded-none border-[#FF3B30] bg-transparent hover:bg-[#1a0a0a] text-[#FF3B30] font-display uppercase tracking-widest"
              >
                <XOctagon size={16} className="mr-2" />
                Avbryt session
              </Button>
            )}
            {!session.completed && goalkeeper.role !== "viewer" && (
              <Button
                data-testid="complete-match-btn"
                onClick={() => setCompleteOpen(true)}
                variant="outline"
                className="h-11 rounded-none border-[#FFD60A] bg-transparent hover:bg-[#1a1a0a] text-[#FFD60A] font-display uppercase tracking-widest"
              >
                <Flag size={16} className="mr-2" />
                Match klar
              </Button>
            )}
            <Button
              data-testid="match-mode-toggle"
              data-active={matchMode}
              onClick={toggleMatchMode}
              variant="outline"
              className="match-mode-toggle h-11 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
              title="Match-läge – större knappar för surfplatta"
            >
              <Maximize2 size={16} className="mr-2" />
              {matchMode ? "Normalt" : "Match-läge"}
            </Button>
            <Button
              data-testid="share-open-btn"
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="h-11 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white"
            >
              <Share2 size={16} className="mr-2" />
              Dela
            </Button>
          </>
        }
      />

      <main className={`${matchMode ? "max-w-[1400px]" : "max-w-[1600px]"} mx-auto px-6 py-8`}>
        <div className={`grid grid-cols-1 gap-6 ${matchMode ? "" : "lg:grid-cols-12"}`}>
          {/* LEFT — INPUT PANEL */}
          <section className={`card-tactical p-6 space-y-6 ${matchMode ? "" : "lg:col-span-5"}`}>
            <div className="flex items-center justify-between">
              <div className="section-hdr">
                <span className="marker" />
                Registrera skott
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-widest font-display">
                {session.session_type === "match" ? "Match" : "Träning"}
                {session.opponent ? ` · vs ${session.opponent}` : ""}
              </div>
            </div>

            {goalkeeper.role === "viewer" && !session.completed && (
              <div
                data-testid="viewer-banner"
                className="border border-[#34C759] bg-[#0f1a12] p-4"
              >
                <div className="font-display uppercase tracking-widest text-sm text-[#34C759]">
                  Läs-läge
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">
                  Du har läsrättigheter på denna profil – du kan inte logga skott
                  eller ändra matchen. Besök Dagbok för att lägga till en
                  anteckning.
                </div>
              </div>
            )}

            {session.completed && (
              <div
                data-testid="completed-banner"
                className="border border-[#FFD60A] bg-[#1a1a0a] p-4 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="flex items-center gap-3">
                  <Flag size={18} className="text-[#FFD60A]" />
                  <div>
                    <div className="font-display uppercase tracking-widest text-sm text-[#FFD60A]">
                      Matchen är avslutad
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Registrering är låst. Öppna matchen under "Utvärdera
                      sessioner" för att se statistiken.
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="goto-evaluate-btn"
                  onClick={() => navigate(`/gk/${gkId}/evaluate/session/${sessId}`)}
                  className="h-10 rounded-none bg-[#FFD60A] hover:bg-[#e0bd00] text-black font-display uppercase tracking-widest text-xs"
                >
                  Öppna utvärdering
                </Button>
              </div>
            )}

            {cfg.zone && !isStraff && (
              <div ref={sectionRefs.zone} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Skott från zon
                </div>
                <div className={`court-wrap ${matchMode ? "max-w-full" : "max-w-[520px]"}`}>
                  <HandballCourt value={zone} onChange={handleZoneChange} />
                </div>
                {zone && (
                  <div
                    data-testid="zone-selected-label"
                    className="text-[11px] uppercase tracking-[0.2em] text-[#34C759] font-display pt-1"
                  >
                    Vald zon · {zone}
                  </div>
                )}
              </div>
            )}
            {cfg.zone && isStraff && (
              <div
                data-testid="straff-notice"
                className="border border-[#27272A] bg-[#171717] px-4 py-3 flex items-center gap-3"
              >
                <span className="w-2 h-2 bg-[#FFD60A] inline-block" />
                <span className="text-xs uppercase tracking-[0.2em] font-display text-zinc-300">
                  Straff – skottet tas från straffpunkten. Ingen zon.
                </span>
              </div>
            )}

            {cfg.distance && (
              <div ref={sectionRefs.distance} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Avstånd
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {DISTANCES.filter(
                    (d) => !cfg.distances || cfg.distances.includes(d.value)
                  ).map((d) => (
                    <button
                      key={d.value}
                      data-testid={`distance-${d.value}`}
                      data-active={distance === d.value}
                      onClick={() => selectDistance(d.value)}
                      className="seg-btn"
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {cfg.shooter && (
              <div ref={sectionRefs.shooter} className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    data-testid="log-shooter-checkbox"
                    type="checkbox"
                    checked={logShooter}
                    onChange={(e) => {
                      setLogShooter(e.target.checked);
                      if (!e.target.checked) setShooter("");
                    }}
                    className="w-7 h-7 accent-[#34C759]"
                  />
                  <span className="text-xl uppercase tracking-[0.2em] text-zinc-300 font-display font-black">
                    Logga skytt
                  </span>
                </label>
                {logShooter && (
                  <input
                    data-testid="shooter-input"
                    type="number"
                    min={1}
                    max={99}
                    value={shooter}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") { setShooter(""); return; }
                      const n = Math.min(99, Math.max(1, parseInt(v, 10) || 1));
                      setShooter(String(n));
                    }}
                    placeholder="1–99"
                    className="w-64 h-24 px-4 bg-black border border-[#3F3F46] text-white font-display font-black text-5xl text-center focus:outline-none focus:border-[#34C759]"
                  />
                )}
              </div>
            )}

            {cfg.placement && (
              <div ref={sectionRefs.placement} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Skottplacering (1 = uppe vänster, 9 = nere höger)
                  {cfg.shooter && logShooter && (
                    <span className="text-[#FF3B30] normal-case ml-2 tracking-normal">
                      · rutorna 10–15 utanför registrerar miss
                    </span>
                  )}
                </div>
                <div className={`goalframe-wrap ${matchMode ? "max-w-full" : "max-w-[520px]"}`}>
                  <GoalFrame
                    placement={placement}
                    onSelect={selectPlacement}
                    selectable
                    showMissFrame={cfg.shooter && logShooter}
                    missSelectable={cfg.shooter && logShooter}
                    onMissSelect={selectPlacement}
                    testIdPrefix="input-cell"
                    testId="input-goal-grid"
                    missTestIdPrefix="input-miss-cell"
                  />
                </div>
              </div>
            )}

            {cfg.contact && !isMiss && (
              <div ref={sectionRefs.contact} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Kontakt vid skott
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="contact-yes"
                    data-active={contact === true}
                    onClick={() => chooseContact(true)}
                    className="seg-btn"
                    style={{ minHeight: 56 }}
                  >
                    Ja
                  </button>
                  <button
                    data-testid="contact-no"
                    data-active={contact === false}
                    onClick={() => chooseContact(false)}
                    className="seg-btn"
                    style={{ minHeight: 56 }}
                  >
                    Nej
                  </button>
                </div>
              </div>
            )}

            {cfg.lob && !isMiss && (
              <div ref={sectionRefs.lob} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Lobb
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="lob-yes"
                    data-active={lob === true}
                    onClick={() => chooseLob(true)}
                    className="seg-btn"
                    style={{ minHeight: 56 }}
                  >
                    Ja
                  </button>
                  <button
                    data-testid="lob-no"
                    data-active={lob === false}
                    onClick={() => chooseLob(false)}
                    className="seg-btn"
                    style={{ minHeight: 56 }}
                  >
                    Nej
                  </button>
                </div>
              </div>
            )}

            {!isMiss && (
              <div ref={sectionRefs.saved} className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Räddning?
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="saved-yes"
                    data-active={saved === true}
                    onClick={() => chooseSaved(true)}
                    className="seg-btn success"
                    style={{ minHeight: 64, fontSize: "1.5rem" }}
                  >
                    Ja
                  </button>
                  <button
                    data-testid="saved-no"
                    data-active={saved === false}
                    onClick={() => chooseSaved(false)}
                    className="seg-btn danger"
                    style={{ minHeight: 64, fontSize: "1.5rem" }}
                  >
                    Nej
                  </button>
                </div>
              </div>
            )}
            {isMiss && (
              <div
                data-testid="miss-notice"
                className="border border-[#FF3B30] bg-[#1a0a0a] px-4 py-3 flex items-center gap-3"
              >
                <span className="w-2 h-2 bg-[#FF3B30] inline-block" />
                <span className="text-xs uppercase tracking-[0.2em] font-display text-zinc-300">
                  Skott utanför målramen – ingen räddning, inget mål
                </span>
              </div>
            )}

            <div ref={sectionRefs.submit} className="flex gap-3 pt-2">
              <Button
                data-testid="register-shot-btn"
                onClick={registerShot}
                disabled={!inputReady || saving || session.completed}
                className="flex-1 h-14 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest text-base disabled:opacity-40"
              >
                Registrera skott
              </Button>
              <Button
                data-testid="undo-shot-btn"
                onClick={undoLast}
                disabled={shots.length === 0 || session.completed}
                variant="outline"
                className="h-14 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white disabled:opacity-40"
              >
                <Undo2 size={18} />
              </Button>
            </div>
          </section>

          {/* RIGHT — LIVE STATS (below inputs in match-mode) */}
          <section className={`space-y-6 ${matchMode ? "" : "lg:col-span-7"}`}>
            <StatsView
              stats={stats}
              testIdPrefix="stats"
              showMissFrame={filters.shooter != null}
            />
            <StatsFilters
              goalkeeperId={gkId}
              sessionId={sessId}
              dateFrom={null}
              dateTo={null}
              value={filters}
              onChange={setFilters}
            />
          </section>
        </div>
      </main>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        goalkeeperId={gkId}
        goalkeeperName={goalkeeper.name}
        sessionId={sessId}
        sessionName={session.name}
      />

      <AlertDialog open={completeOpen} onOpenChange={(o) => { setCompleteOpen(o); if (!o) { setFinalScore(""); setMatchNotes(""); } }}>
        <AlertDialogContent
          className="bg-[#111] border-[#27272A] text-white"
          data-testid="complete-match-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest text-xl">
              Match klar
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Registreringen låses. Slutresultat och anteckningar visas i
              analys­vyn efter matchen.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label
                htmlFor="final-score-input"
                className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display block"
              >
                Slutresultat (valfritt)
              </label>
              <input
                id="final-score-input"
                data-testid="final-score-input"
                type="text"
                value={finalScore}
                onChange={(e) => setFinalScore(e.target.value)}
                placeholder="ex. 28–24"
                className="w-full h-12 px-3 bg-black border border-[#3F3F46] text-white font-display font-black text-lg focus:outline-none focus:border-[#FFD60A]"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="match-notes-input"
                className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display block"
              >
                Kommentar efter match (valfritt)
              </label>
              <textarea
                id="match-notes-input"
                data-testid="match-notes-input"
                value={matchNotes}
                onChange={(e) => setMatchNotes(e.target.value)}
                placeholder="Tankar, spelare att komma ihåg, styrkor/svagheter…"
                rows={4}
                className="w-full px-3 py-2 bg-black border border-[#3F3F46] text-white text-sm focus:outline-none focus:border-[#FFD60A] resize-none"
              />
            </div>
          </div>

          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="complete-cancel-btn"
              className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
            >
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="complete-confirm-btn"
              onClick={completeMatch}
              className="rounded-none bg-[#FFD60A] hover:bg-[#e0bd00] text-black font-display uppercase tracking-widest"
            >
              <Flag size={14} className="mr-2" />
              Ja, match klar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent
          data-testid="cancel-session-confirm"
          className="bg-[#111] border-[#27272A] text-white rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest text-xl">
              Är du säker?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              All data i sessionen försvinner permanent och kan inte återskapas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="cancel-session-cancel"
              className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
            >
              Nej, fortsätt session
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-session-confirm-btn"
              onClick={cancelSession}
              disabled={cancelling}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e33228] text-white font-display uppercase tracking-widest"
            >
              {cancelling ? "Avbryter…" : "Ja, ta bort data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <footer className="max-w-[1600px] mx-auto px-6 py-6 text-[10px] text-zinc-600 uppercase tracking-[0.25em] font-display">
        {totalShots} skott i denna session
      </footer>
    </div>
  );
}
